import React, { useEffect, useRef, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, MenuItem, Paper, Stack, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, Typography, useMediaQuery,
} from '@mui/material';
import DashboardLayout from '../components/DashboardLayout';
import AdminActionGroup from '../components/AdminActionGroup';
import api from '../services/api';

const root = '/tutor-capabilities/admin';
const idOf = value => String(value?._id || value || '');
const person = value => [value?.name, value?.surname].filter(Boolean).join(' ') || value?.email || 'Tutor unavailable';
const errorMessage = error => error?.response?.data?.message || 'Unable to load tutor capabilities. Please try again.';

function CapabilityList({ entries = [], title }) {
  return <Box component="section" sx={{ mb: 3 }}>
    <Typography component="h3" variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>{title}</Typography>
    {!entries.length ? <Typography color="text.secondary">No capabilities recorded.</Typography> :
      <Stack spacing={1}>{entries.map((entry, index) => <Paper variant="outlined" key={index} sx={{ p: 1.5, overflowWrap: 'anywhere' }}>
        <Typography fontWeight={700}>{entry.subject}</Typography>
        <Typography variant="body2">{entry.curriculum} · Grade {entry.grade === 0 ? 'R' : entry.grade} · {entry.deliveryMode === 'in-person' ? 'In-person' : 'Online'}</Typography>
        {entry.curriculumLevel && <Typography variant="body2">Level: {entry.curriculumLevel}</Typography>}
        {entry.curriculumSubLevel && <Typography variant="body2">Sub-level: {entry.curriculumSubLevel}</Typography>}
        {entry.deliveryMode === 'in-person' && entry.serviceArea && <Typography variant="body2" color="text.secondary">
          {entry.serviceArea.province} · {(entry.serviceArea.postalCodes || []).join(', ')}
        </Typography>}
      </Paper>)}</Stack>}
  </Box>;
}

export default function TutorCapabilities() {
  const compact = useMediaQuery('(max-width: 599px)');
  const [state, setState] = useState('pending');
  const [page, setPage] = useState(1);
  const [refresh, setRefresh] = useState(0);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [mode, setMode] = useState('off');
  const [loading, setLoading] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [selection, setSelection] = useState(null);
  const [profile, setProfile] = useState(null);
  const [reason, setReason] = useState('');
  const [reviewError, setReviewError] = useState('');
  const [reviewConflict, setReviewConflict] = useState(false);
  const [busy, setBusy] = useState(false);
  const detailRequest = useRef(0);

  useEffect(() => {
    let current = true;
    setLoading(true); setError('');
    api.get(root, { params: { state, page, limit: 25 } }).then(({ data }) => {
      if (!current) return;
      setItems(data.items || []); setTotal(data.total || 0); setMode(data.rolloutMode || 'off');
    }).catch(error => { if (current) { setError(errorMessage(error)); setItems([]); setTotal(0); } })
      .finally(() => { if (current) setLoading(false); });
    return () => { current = false; };
  }, [state, page, refresh]);
  useEffect(() => () => { detailRequest.current += 1; }, []);

  const open = async row => {
    const requestId = ++detailRequest.current;
    setSelection(row); setProfile(null); setLoadingDetail(true); setReviewError(''); setReviewConflict(false); setReason(''); setNotice('');
    try {
      const { data } = await api.get(`${root}/${idOf(row.tutorId)}`);
      if (requestId !== detailRequest.current) return;
      setProfile(data.profile); setMode(data.rolloutMode || 'off');
      if (!data.profile) setReviewError('This capability record is no longer available. Refresh the queue.');
    } catch (error) { if (requestId === detailRequest.current) setReviewError(errorMessage(error)); }
    finally { if (requestId === detailRequest.current) setLoadingDetail(false); }
  };
  const close = () => {
    if (busy) return;
    detailRequest.current += 1; setSelection(null); setProfile(null); setLoadingDetail(false); setReviewError('');
  };
  const act = async (action, enabled) => {
    if (busy || reviewConflict || !profile || !reason.trim()) return;
    setBusy(true); setReviewError(''); setNotice('');
    try {
      const rollout = action === 'rollout';
      const { data } = await api.post(`${root}/${idOf(profile.tutorId)}/${rollout ? 'rollout' : 'review'}`, {
        revision: profile.revision, reason: reason.trim(), ...(rollout ? { enabled } : { decision: action }),
      });
      setProfile(data.profile); setReason(''); setRefresh(value => value + 1);
      setNotice(rollout ? 'Controlled-rollout approval updated. Global matching settings are unchanged.' :
        action === 'approve' ? 'Capabilities approved. Controlled-rollout approval is still required.' : 'Submission rejected. Previously approved capabilities are unchanged.');
    } catch (error) { setReviewError(errorMessage(error)); setReviewConflict(error?.response?.status === 409); }
    finally { setBusy(false); }
  };
  const canReview = profile?.pending?.status === 'pending';
  const disabled = busy || reviewConflict || !reason.trim() || reason.length > 1000;

  return <DashboardLayout>
    <Stack spacing={2} sx={{ minWidth: 0 }}>
      <Alert severity="info">Apex Verified is unchanged. Matching mode: {mode}. Capability approval and controlled-rollout approval are separate.</Alert>
      {error && <Alert severity="error">{error}</Alert>}
      {notice && !selection && <Alert severity="success">{notice}</Alert>}
      <AdminActionGroup>
        <TextField select label="Review state" value={state} sx={{ minWidth: 220 }} onChange={event => { setState(event.target.value); setPage(1); }}>
          {['pending', 'approved', 'rejected', 'all'].map(value => <MenuItem key={value} value={value}>{value === 'all' ? 'All records' : value[0].toUpperCase() + value.slice(1)}</MenuItem>)}
        </TextField>
        <Button variant="outlined" onClick={() => setRefresh(value => value + 1)} disabled={loading}>Refresh queue</Button>
      </AdminActionGroup>
      {loading ? <Box role="status" sx={{ p: 3 }}><CircularProgress size={24} aria-label="Loading capability queue" /></Box> :
        !items.length ? <Paper variant="outlined" sx={{ p: 3 }}>No capability records match this review state.</Paper> : compact ?
          <Stack spacing={2}>{items.map(row => <Paper key={row._id} variant="outlined" sx={{ p: 2, overflowWrap: 'anywhere' }}>
            <Typography fontWeight={700}>{person(row.tutorId)}</Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>{row.tutorId?.email}</Typography>
            <Chip size="small" label={row.pending?.status || 'Reviewed'} variant="outlined" />
            <Typography variant="body2" sx={{ mt: 1 }}>Approved combinations: {row.activeEntries?.length || 0}</Typography>
            <Typography variant="body2">Rollout: {row.rolloutApproved ? 'Approved for cohort' : 'Not approved'}</Typography>
            <Button variant="outlined" sx={{ mt: 2 }} disabled={loadingDetail || !row.tutorId} onClick={() => open(row)} aria-label={`Review ${person(row.tutorId)}`}>Review</Button>
          </Paper>)}</Stack> :
          <TableContainer component={Paper} variant="outlined">
            <Table aria-label="Tutor capability review queue">
              <TableHead><TableRow><TableCell>Tutor</TableCell><TableCell>Submission</TableCell><TableCell>Approved combinations</TableCell><TableCell>Rollout approval</TableCell><TableCell>Action</TableCell></TableRow></TableHead>
              <TableBody>{items.map(row => <TableRow key={row._id}>
                <TableCell sx={{ overflowWrap: 'anywhere', minWidth: 160 }}><Typography fontWeight={700}>{person(row.tutorId)}</Typography><Typography variant="body2">{row.tutorId?.email}</Typography></TableCell>
                <TableCell><Chip size="small" label={row.pending?.status || 'Reviewed'} variant="outlined" /></TableCell>
                <TableCell>{row.activeEntries?.length || 0}</TableCell>
                <TableCell>{row.rolloutApproved ? 'Approved for cohort' : 'Not approved'}</TableCell>
                <TableCell><Button variant="outlined" disabled={loadingDetail || !row.tutorId} onClick={() => open(row)} aria-label={`Review ${person(row.tutorId)}`}>Review</Button></TableCell>
              </TableRow>)}</TableBody>
            </Table>
          </TableContainer>}
      <AdminActionGroup>
        <Button disabled={loading || page === 1} onClick={() => setPage(value => value - 1)}>Previous page</Button>
        <Typography>Page {page} · {total} records</Typography>
        <Button disabled={loading || page * 25 >= total} onClick={() => setPage(value => value + 1)}>Next page</Button>
      </AdminActionGroup>
    </Stack>
    <Dialog open={Boolean(selection)} onClose={close} fullWidth maxWidth="md" aria-labelledby="capability-review-title">
      <DialogTitle id="capability-review-title">Capabilities: {person(selection?.tutorId)}</DialogTitle>
      <DialogContent dividers sx={{ overflowWrap: 'anywhere' }}>
        {loadingDetail && <CircularProgress aria-label="Loading capability detail" size={24} />}
        {reviewError && <Alert severity="error" sx={{ mb: 2 }}>{reviewError} Use Refresh details before retrying.</Alert>}
        {notice && <Alert severity="success" sx={{ mb: 2 }}>{notice}</Alert>}
        {profile && <>
          <Typography variant="body2" sx={{ mb: 2 }}>Revision {profile.revision} · Approved revision {profile.approvedRevision || 0}</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
            <CapabilityList title="Currently approved" entries={profile.activeEntries} />
            <Box><CapabilityList title={profile.pending?.status === 'rejected' ? 'Rejected submission' : 'Pending submission'} entries={profile.pending?.entries} />
              {profile.pending && <Typography variant="body2" sx={{ mb: 2 }}>Source: {profile.pending.source?.kind === 'legacy_migration' ? 'Legacy migration' : 'Explicit submission'}{profile.pending.reason ? ` · ${profile.pending.reason}` : ''}</Typography>}
            </Box>
          </Box>
          <TextField fullWidth multiline minRows={2} label="Review reason" value={reason} disabled={busy}
            onChange={event => setReason(event.target.value)} inputProps={{ maxLength: 1000 }} helperText="Required for approval, rejection or changing rollout approval." />
          <AdminActionGroup sx={{ mt: 2 }}>
            {canReview && <><Button variant="contained" disabled={disabled} onClick={() => act('approve')}>Approve capabilities</Button>
              <Button variant="outlined" color="error" disabled={disabled} onClick={() => act('reject')}>Reject submission</Button></>}
            {profile.approvedRevision > 0 && <Button variant="outlined" disabled={disabled} onClick={() => act('rollout', !profile.rolloutApproved)}>
              {profile.rolloutApproved ? 'Withdraw rollout approval' : 'Approve for controlled rollout'}
            </Button>}
          </AdminActionGroup>
          <Typography component="h3" variant="subtitle1" fontWeight={700} sx={{ mt: 3 }}>Recent review history</Typography>
          {(profile.history || []).slice(-20).reverse().map((entry, index) => <Box key={index} sx={{ mt: 1 }}>
            <Typography variant="body2" fontWeight={700}>{entry.action.replace(/_/g, ' ')} · Revision {entry.revision}</Typography>
            <Typography variant="body2">{entry.reason}</Typography>
            <Typography variant="caption">{entry.at ? new Date(entry.at).toLocaleString() : ''} · {entry.actorRole}</Typography>
          </Box>)}
        </>}
      </DialogContent>
      <DialogActions sx={{ flexWrap: 'wrap', gap: 1, p: 2 }}>
        <Button variant="outlined" disabled={busy || loadingDetail} onClick={() => open(selection)}>Refresh details</Button>
        <Button disabled={busy} onClick={close}>Close</Button>
      </DialogActions>
    </Dialog>
  </DashboardLayout>;
}
